import React from 'react';
import { Badge } from 'react-bootstrap';
import { ITravelerProfile, IProfileArchetype } from '../../types/profile.types';
import Button from '../UI/button';
import './TravelerProfileCard.css';

interface TravelerProfileCardProps {
  profile: ITravelerProfile;
  archetype?: IProfileArchetype;
  onEdit: (profile: ITravelerProfile) => void;
  onDelete: (id: string) => void;
  onSetDefault: (id: string) => void;
}

const BUDGET_LABEL: Record<ITravelerProfile['budgetLevel'], string> = {
  budget: 'Budget',
  moderate: 'Moderate',
  luxury: 'Luxury',
  ultra_luxury: 'Ultra Luxury'
};

const TravelerProfileCard: React.FC<TravelerProfileCardProps> = ({
  profile,
  archetype,
  onEdit,
  onDelete,
  onSetDefault
}) => {
  return (
    <div
      className={`tp-card${profile.isDefault ? ' tp-card--default' : ''}`}
      style={
        archetype
          ? ({
              '--archetype-color': archetype.colorTheme
            } as React.CSSProperties)
          : undefined
      }
    >
      <div className='tp-card__header'>
        <div
          className='tp-card__archetype-badge'
          style={archetype ? { background: archetype.colorTheme } : undefined}
        >
          <span className='tp-card__emoji'>{archetype?.emoji ?? '🧳'}</span>
        </div>
        <div className='tp-card__title-group'>
          <h5 className='tp-card__name'>{profile.name}</h5>
          <span className='tp-card__archetype-name'>
            {profile.archetypeName}
          </span>
        </div>
        {profile.isDefault && (
          <span
            className='tp-card__star'
            title='Default profile'
          >
            ⭐
          </span>
        )}
      </div>

      {profile.description && (
        <p className='tp-card__description'>{profile.description}</p>
      )}

      <div className='tp-card__badges'>
        <span
          className={`tp-badge-budget tp-badge-budget--${profile.budgetLevel}`}
        >
          {BUDGET_LABEL[profile.budgetLevel]}
        </span>
        <Badge
          bg='light'
          text='dark'
          className='tp-badge-pill'
        >
          {profile.pacePreference} pace
        </Badge>
        <Badge
          bg='light'
          text='dark'
          className='tp-badge-pill'
        >
          {profile.travelStyle}
        </Badge>
      </div>

      {(profile.primaryTags?.length ?? 0) > 0 && (
        <div className='tp-card__tags'>
          {(profile.primaryTags ?? []).slice(0, 5).map((tag) => (
            <span
              key={tag}
              className='tp-card__tag'
            >
              {tag}
            </span>
          ))}
          {(profile.primaryTags?.length ?? 0) > 5 && (
            <span className='tp-card__tag tp-card__tag--more'>
              +{(profile.primaryTags?.length ?? 0) - 5}
            </span>
          )}
        </div>
      )}

      <div className='tp-card__actions'>
        <Button
          variant='outline-primary'
          size='sm'
          onClick={() => onEdit(profile)}
        >
          Edit
        </Button>
        {!profile.isDefault && (
          <Button
            variant='outline-secondary'
            size='sm'
            onClick={() => onSetDefault(profile._id)}
          >
            Make Default
          </Button>
        )}
        <Button
          variant='outline-danger'
          size='sm'
          onClick={() => onDelete(profile._id)}
        >
          Delete
        </Button>
      </div>
    </div>
  );
};

export default TravelerProfileCard;
